import type { AppointmentsMetricDefinition, LeadStatsChoicesDto, LeadsMetricDefinition } from '@lib/dto/lead-stats.dto'
import { GHL_OPPORTUNITY_STATUSES, GHL_OPPORTUNITY_STATUS_LABEL } from '@lib/enums/ghl-opportunity-status.enum'
import { GHL_APPOINTMENT_STATUSES, GHL_APPOINTMENT_STATUS_LABEL } from '@lib/enums/ghl-appointment-status.enum'
import { missingNames, normalizeGhlName } from '@lib/helpers/lead-stats-names'
import { Field } from '@/components/ui/field'
import { MultiSelect, type MultiSelectOption } from '@/components/ui/multi-select'
import { Select } from '@/components/ui/select'

/**
 * The ingredients of a number (docs/gohighlevel-stats.spec.md, "Numbers"),
 * chosen by the names GoHighLevel shows. Shared by the company's definitions and
 * a venue's adjustments.
 */

/** Toggle a name in a list, matching without regard to case. */
export function toggleName(names: readonly string[], name: string): string[] {
  const key = normalizeGhlName(name)
  return names.some((n) => normalizeGhlName(n) === key) ? names.filter((n) => normalizeGhlName(n) !== key) : [...names, name]
}

/**
 * Options from the names GoHighLevel has, plus any chosen name it no longer has,
 * marked, so it can be seen and taken out rather than silently matching nothing.
 */
function nameOptions(available: readonly string[], chosen: readonly string[]): MultiSelectOption[] {
  const gone = missingNames(chosen, available)
  return [
    ...available.map((name) => ({ value: name, label: name })),
    ...gone.map((name) => ({ value: name, label: `${name} (not found in GoHighLevel)` })),
  ]
}

/** The chosen names as the options spell them, so the checklist ticks them. */
function chosenValues(options: readonly MultiSelectOption[], chosen: readonly string[]): string[] {
  const keys = new Set(chosen.map(normalizeGhlName))
  return options.filter((o) => keys.has(normalizeGhlName(o.value))).map((o) => o.value)
}

/** A list of GoHighLevel names, chosen from what the account has. */
export function NameField({
  id,
  label,
  hint,
  available,
  chosen,
  onChange,
  placeholder,
}: {
  id: string
  label: string
  hint?: string | undefined
  available: readonly string[]
  chosen: readonly string[]
  onChange: (names: string[]) => void
  placeholder?: string | undefined
}) {
  const options = nameOptions(available, chosen)
  const gone = missingNames(chosen, available)
  return (
    <Field label={label} htmlFor={id} hint={hint} error={gone.length > 0 ? `Not found in GoHighLevel: ${gone.join(', ')}` : undefined}>
      <MultiSelect
        id={id}
        options={options}
        selected={chosenValues(options, chosen)}
        onToggle={(name) => onChange(toggleName(chosen, name))}
        summary={(picked) =>
          picked.length > 2
            ? `${picked[0]?.value ?? ''}, ${picked[1]?.value ?? ''} and ${String(picked.length - 2)} more`
            : picked.map((o) => o.value).join(', ')
        }
        {...(placeholder === undefined ? {} : { placeholder })}
      />
    </Field>
  )
}

/** Every stage name the account's pipelines have, once each. */
export function stageNames(choices: LeadStatsChoicesDto): string[] {
  const seen = new Map<string, string>()
  for (const pipeline of choices.pipelines) {
    for (const stage of pipeline.stages) if (!seen.has(normalizeGhlName(stage))) seen.set(normalizeGhlName(stage), stage)
  }
  return [...seen.values()]
}

const RESULT_OPTIONS: MultiSelectOption[] = GHL_OPPORTUNITY_STATUSES.map((s) => ({ value: s, label: GHL_OPPORTUNITY_STATUS_LABEL[s] }))
const APPOINTMENT_STATUS_OPTIONS: MultiSelectOption[] = GHL_APPOINTMENT_STATUSES.map((s) => ({
  value: s,
  label: GHL_APPOINTMENT_STATUS_LABEL[s],
}))

/** A number that counts couples: stages, results, tags and sources. */
export function LeadsDefinitionFields({
  idPrefix,
  definition,
  choices,
  onChange,
}: {
  idPrefix: string
  definition: LeadsMetricDefinition
  choices: LeadStatsChoicesDto
  onChange: (definition: LeadsMetricDefinition) => void
}) {
  return (
    <div className="grid gap-3 md:grid-cols-2">
      <NameField
        id={`${idPrefix}-stages`}
        label="On any of these stages"
        available={stageNames(choices)}
        chosen={definition.stages}
        onChange={(stages) => onChange({ ...definition, stages })}
        placeholder="Any stage"
      />
      <Field label="With any of these results" htmlFor={`${idPrefix}-results`}>
        <MultiSelect
          id={`${idPrefix}-results`}
          options={RESULT_OPTIONS}
          selected={definition.results}
          onToggle={(value) => {
            const status = GHL_OPPORTUNITY_STATUSES.find((s) => s === value)
            if (status === undefined) return
            const results = definition.results.includes(status)
              ? definition.results.filter((r) => r !== status)
              : [...definition.results, status]
            onChange({ ...definition, results })
          }}
          placeholder="Any result"
        />
      </Field>
      <NameField
        id={`${idPrefix}-tags-any`}
        label="Tagged with any of"
        available={choices.tags}
        chosen={definition.tagsAny}
        onChange={(tagsAny) => onChange({ ...definition, tagsAny })}
        placeholder="Any tags"
      />
      <NameField
        id={`${idPrefix}-tags-none`}
        label="Tagged with none of"
        available={choices.tags}
        chosen={definition.tagsNone}
        onChange={(tagsNone) => onChange({ ...definition, tagsNone })}
        placeholder="No tags left out"
      />
      <NameField
        id={`${idPrefix}-sources`}
        label="From any of these lead sources"
        available={choices.sources}
        chosen={definition.sources}
        onChange={(sources) => onChange({ ...definition, sources })}
        placeholder="Any source"
      />
    </div>
  )
}

/** A number that counts appointments on chosen calendars. */
export function AppointmentsDefinitionFields({
  idPrefix,
  definition,
  choices,
  onChange,
}: {
  idPrefix: string
  definition: AppointmentsMetricDefinition
  choices: LeadStatsChoicesDto
  onChange: (definition: AppointmentsMetricDefinition) => void
}) {
  return (
    <div className="grid gap-3 md:grid-cols-2">
      <NameField
        id={`${idPrefix}-calendars`}
        label="On these calendars"
        hint={choices.calendarsReadable ? undefined : 'A key that can’t see calendars counts none. Replace it with one that can.'}
        available={choices.calendars}
        chosen={definition.calendars}
        onChange={(calendars) => onChange({ ...definition, calendars })}
        placeholder="Choose calendars"
      />
      <Field label="With any of these statuses" htmlFor={`${idPrefix}-statuses`}>
        <MultiSelect
          id={`${idPrefix}-statuses`}
          options={APPOINTMENT_STATUS_OPTIONS}
          selected={definition.statuses}
          onToggle={(value) => {
            const status = GHL_APPOINTMENT_STATUSES.find((s) => s === value)
            if (status === undefined) return
            const statuses = definition.statuses.includes(status)
              ? definition.statuses.filter((s) => s !== status)
              : [...definition.statuses, status]
            onChange({ ...definition, statuses })
          }}
          placeholder="Any status"
        />
      </Field>
      <Field label="Counted on" htmlFor={`${idPrefix}-count-by`}>
        <Select
          id={`${idPrefix}-count-by`}
          value={definition.countBy}
          onChange={(e) => onChange({ ...definition, countBy: e.target.value === 'held' ? 'held' : 'booked' })}
        >
          <option value="booked">The day it was booked</option>
          <option value="held">The day it is for</option>
        </Select>
      </Field>
    </div>
  )
}
